import { useEffect, useState } from 'react';
import { format, subDays } from 'date-fns';
import AdminSectionTitle from '../components/AdminSectionTitle';
import ButtonGroup from '../components/atoms/ButtonGroup';
import Summary from '../components/Summary';
import TitleNumberCard from '../components/atoms/TitleNumberCard';
import WireTable from '../components/WireTable';
import EventsTable from '../components/EventsTable';
import { useGetWiresRequestsQuery, useGetEventsByQueryQuery, useGetAvanttiPaymentReportQuery } from '../store';
import isMobile from '../utils/isMobile';
import { formatCurrency } from '../utils/shared';
import CustomModal from '../components/atoms/CustomModal';
import Input from '../components/atoms/Input';
import Button from '../components/atoms/Button';
import { EventPublicStatus } from '../models/event';

const SuperAdmin = () => {
  const { data: pendingWires } = useGetWiresRequestsQuery({ pending: true });
  const { data: eventsByQuery } = useGetEventsByQueryQuery({});
  const [startDate, setStartDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [selectedDateRange, setSelectedDateRange] = useState<'Month-to-date' | 'Últimos 7 días' | 'Últimos 30 días' | 'Otro'>('Month-to-date');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fetchReport, setFetchReport] = useState(true);
  const {
    data,
  } = useGetAvanttiPaymentReportQuery(
    { startDate, endDate },
    { skip: !fetchReport },
  );

  useEffect(() => {
    if (selectedDateRange === 'Month-to-date') {
      setStartDate(format(new Date(), 'yyyy-MM-01'));
      setEndDate(format(new Date(), 'yyyy-MM-dd'));
    }
    if (selectedDateRange === 'Últimos 7 días') {
      setStartDate(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
      setEndDate(format(new Date(), 'yyyy-MM-dd'));
    }
    if (selectedDateRange === 'Últimos 30 días') {
      setStartDate(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
      setEndDate(format(new Date(), 'yyyy-MM-dd'));
    }
    if (selectedDateRange === 'Otro') {
      setFetchReport(false);
      setIsModalOpen(true);
      setStartDate(format(new Date(), 'yyyy-MM-dd'));
      setEndDate(format(new Date(), 'yyyy-MM-dd'));
    }
  }, [selectedDateRange]);

  const summary = [
    {
      title: 'Balance plataforma',
      value: formatCurrency((data?.processed_manual_amount ?? 0) + (data?.processed_online_amount ?? 0)),
    },
    {
      title: 'Procesado online',
      value: formatCurrency(data?.processed_online_amount || 0),
    },
    {
      title: 'Procesado online split',
      value: formatCurrency(data?.processed_split_amount || 0),
    },
    {
      title: 'Procesado online recibido',
      value: formatCurrency(data?.processed_received_amount_online || 0),
    },
    {
      title: 'Procesado de forma manual',
      value: formatCurrency(data?.processed_manual_amount || 0),
    },
    {
      title: 'Ingresos Avantti',
      value: formatCurrency(data?.avantti_amount || 0),
    },
    {
      title: 'Comisiones MercadoPago',
      value: formatCurrency(data?.gateway_amount || 0),
    },
  ];

  const sortedWires = pendingWires ? [...pendingWires].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) : [];

  const filteredEvents = eventsByQuery
    ? [...eventsByQuery].filter((event) => (event.public_status === EventPublicStatus.PendingApproval
      || (event.public_status === EventPublicStatus.Ready
        && new Date(event.updated_at) > new Date(new Date().setDate(new Date().getDate() - 5)))))
      .sort((a, b) => {
        if (a.public_status !== b.public_status) {
          return a.public_status === EventPublicStatus.PendingApproval ? -1 : 1;
        }
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }) : [];

  return (
    <div className="flex flex-col gap-8 my-6">
      <AdminSectionTitle title="Panel de gestión" />
      <div className='max-w-md'>
        <ButtonGroup
          buttons={['Month-to-date', 'Últimos 7 días', 'Últimos 30 días', 'Otro']}
          defaultButton="Month-to-date"
          onButtonChange={(button) => {
            setSelectedDateRange(button as 'Month-to-date' | 'Últimos 7 días' | 'Últimos 30 días' | 'Otro');
          }}
        />
      </div>
      <div className="flex flex-col gap-4">
        <h1>Balance plataforma</h1>
        <div className="flex flex-col lg:flex-row gap-4">
          <div className='w-full lg:w-1/2'><Summary summary={summary} highlight={false} /></div>
          {!isMobile() && <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full'>
            <TitleNumberCard title="Entradas vendidas" number={data?.processed_items_count || 0} />
            <TitleNumberCard title="Ingresos de Avantti" number={formatCurrency(data?.avantti_amount || 0)} />
            <TitleNumberCard title="Ordenes generadas" number={data?.processed_payments || 0} />
            <TitleNumberCard title="Total procesado" number={formatCurrency((data?.processed_manual_amount ?? 0) + (data?.processed_online_amount ?? 0))} />
          </div>}
        </div>
      </div>
      <div className='flex flex-col gap-4'>
        <h1>Eventos pendientes</h1>
        <EventsTable events={filteredEvents ?? []} />
      </div>
      <div className='flex flex-col gap-4'>
        <h1>Transferencias pendientes</h1>
        <WireTable data={sortedWires ?? []} maxToRequest={0} superadmin />
      </div>
      <CustomModal
        isOpen={isModalOpen}
        onRequestClose={() => {
          setIsModalOpen(false);
          setFetchReport(true);
        }}
        hideCancelButton
      >
        <div>
          <h1 className='text-xl font-bold mb-4'>Seleccionar rango de fechas</h1>
          <div className='flex flex-col gap-4 mb-4'>
            <Input
              id="startDate"
              label="Fecha inicio"
              labelAbove
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              id="endDate"
              label="Fecha fin"
              labelAbove
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <Button
            onClick={() => {
              setIsModalOpen(false);
              setFetchReport(true);
            }}
          >
            Aceptar
          </Button>
        </div>
      </CustomModal>
    </div >
  );
};

export default SuperAdmin;
